import { useId } from 'react'
import type { InputHTMLAttributes, ReactNode, Ref } from 'react'

export interface FieldProps {
  label: string
  hint?: string
  error?: string
  required?: boolean
  children: (props: {
    id: string
    'aria-describedby': string | undefined
    'aria-invalid': boolean | undefined
    'aria-required': boolean | undefined
  }) => ReactNode
}

/**
 * Field — label, control, hint, and error as one unit.
 *
 * Bundled deliberately: the error state is where form accessibility is most
 * often lost, because wiring aria-describedby by hand is easy to forget and
 * impossible to notice visually. Here it is impossible to render an error
 * without also announcing it.
 */
export function Field({ label, hint, error, required, children }: FieldProps) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined

  // Error first: when both are present, the correction matters more than the
  // guidance, and a screen reader reads them in this order.
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined

  return (
    <div className="lg-field">
      {/* The label is always visible. Placeholder-as-label disappears the
          moment the user types, which in a 40-field HR form means they can no
          longer tell what they are filling in. */}
      <label className="lg-field__label" htmlFor={id}>
        {label}
        {required && <span className="lg-field__required">Required</span>}
      </label>

      {children({
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        'aria-required': required || undefined,
      })}

      {hint && !error && (
        <span className="lg-field__hint" id={hintId}>
          {hint}
        </span>
      )}

      {error && (
        // role="alert" so a validation failure arriving after submit is
        // announced without the user having to go looking for it.
        <span className="lg-field__error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  )
}

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> & {
  // React 19 passes ref to function components as an ordinary prop, so no
  // forwardRef wrapper is needed — the type just has to admit it.
  ref?: Ref<HTMLInputElement>
}

export function Input(props: InputProps) {
  return <input {...props} className="lg-input" />
}
